import Footer from '../../components/Footer';
import PageHeader from '../../components/PageHeader';
import PetItem from '../../components/PetItem';
import usePetsList from '../../hooks/usePetsList';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterSidebarComponent from '../../components/FilterSidebarComponent';
import Preloader from '../../components/Preloader';

// TODO: done - implement caching for the fetch request

// TODO: handle gracefully when the API call cannot be made

function Adoption() {
  const { t } = useTranslation();
  const skeletonCount = 8;
  const { data: pets, isLoading } = usePetsList();

  const [searchParams, setSearchParams] = useSearchParams();

  const filterState = useMemo(() => ({
    type: searchParams.get('type') || 'all',
    sizes: searchParams.getAll('size'),
    genders: searchParams.getAll('gender'),
    statuses: searchParams.getAll('status'),
    search: searchParams.get('search') || '',
  }), [searchParams]);

  const setFilterState = (update) => {
    setSearchParams(prevParams => {
        const currentObj = {
            type: prevParams.get('type') || 'all',
            sizes: prevParams.getAll('size'),
            genders: prevParams.getAll('gender'),
            statuses: prevParams.getAll('status'),
            search: prevParams.get('search') || ''
        };
        
        const newState = typeof update === 'function' ? update(currentObj) : update;
        
        const newParams = new URLSearchParams();
         if (newState.type && newState.type !== 'all') {
             newParams.set('type', newState.type);
         }
         if (newState.sizes) {
             newState.sizes.forEach(s => newParams.append('size', s));
         }
         if (newState.genders) {
             newState.genders.forEach(g => newParams.append('gender', g));
         }
         if (newState.statuses) {
             newState.statuses.forEach(status => newParams.append('status', status));
         }
         if (newState.search && newState.search.trim() !== '') {
             newParams.set('search', newState.search);
         }
         return newParams;
    }, { replace: true });
  };

  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filtering logic
  const filteredPets = useMemo(() => {
    if (!pets) return [];

    let result = pets;

    if (filterState.search && filterState.search.trim() !== '') {
      const query = filterState.search.toLowerCase().trim();
      result = result.filter(p => p.name?.toLowerCase().includes(query));
    }

    if (filterState.type !== 'all') {
      result = result.filter(p => p.pet_type === filterState.type);
    }

    if (filterState.genders.length > 0) {
      result = result.filter(p =>
        filterState.genders.includes(p.gender?.toLowerCase())
      );
    }

    if (filterState.sizes.length > 0) {
      result = result.filter(p =>
        filterState.sizes.includes(p.size_category?.toLowerCase())
      );
    }

    if (filterState.statuses.length > 0) {
      const normalizedStatuses = filterState.statuses.map(status => status.toLowerCase());
      result = result.filter(p =>
        normalizedStatuses.includes(p.status?.toLowerCase())
      );
    }

    return result;
  }, [pets, filterState]);

  return (
    <div id="top">
      {isLoading && <Preloader />}

      <PageHeader
        title={t('adoptionPage.adopt_pet')}
        page={t('adoptionPage.adopt_pet')}
        pageUrl="adoption"
      />

      <div className="page">
        <div className="container block-padding pt-0 pb-0">
          <div className="col-lg-8 offset-lg-2 text-center">
            <h3>{t('adoptionPage.intro')}</h3>
            <p>{t('adoptionPage.description')}</p>
          </div>
        </div>

        <div className="container mt-4">
          <div className="row">
            {/* MOBILE FILTER BUTTON */}
            <div className="text-center mb-3 d-lg-none">
              <button
                className="btn btn-primary"
                onClick={() => setFiltersOpen(o => !o)}
              >
                {filtersOpen
                  ? t('adoptionPage.hide_filters')
                  : t('adoptionPage.show_filters')}
              </button>
            </div>

            {/* MOBILE COLLAPSIBLE FILTER */}
            {filtersOpen && (
              <div className="col-12 mb-3 d-lg-none">
                <FilterSidebarComponent
                  filterState={filterState}
                  setFilterState={setFilterState}
                />
              </div>
            )}

            {/* DESKTOP FILTER SIDEBAR */}
            <div className="col-lg-3 col-xl-2 col-md-12 mb-4 d-none d-lg-block">
              <FilterSidebarComponent
                filterState={filterState}
                setFilterState={setFilterState}
              />
            </div>

            {/* PET RESULTS */}
            <div className="col-xl-10 col-md-12 col-lg-9">
              <div className="row g-3">
                {isLoading
                  ? Array.from({ length: skeletonCount }).map((_, index) => (
                      <div
                        key={index}
                        className="adopt-card col-sm-6 col-md-4 col-lg-4 col-xl-3 d-flex align-items-stretch mb-4"
                      >
                        <PetItem pet={null} isLoading index={index} />
                      </div>
                    ))
                  : filteredPets.map(pet => (
                      <div
                        key={pet._id}
                        className="adopt-card col-sm-6 col-md-4 col-lg-4 col-xl-3 d-flex align-items-stretch mb-4"
                      >
                        <PetItem pet={pet} allPets={filteredPets} />
                      </div>
                    ))}

                {!isLoading && filteredPets.length === 0 && (
                  <div className="col-12 mt-3">
                    <p className="text-center alert alert-warning">
                      {t('adoptionPage.filter_no_results')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Adoption;
